// @flow

import getter from 'dibs-object-getter';
import { trackEcomEvent } from '../../shared/helpers/tracking';
import type { FavoritesWrapper_viewer as FavoritesViewer } from '../components/__generated__/FavoritesWrapper_viewer.graphql';

export type OverviewFavoriteItemProps = {
    +serviceId: ?string,
    +title: ?string,
    +seller: ?{|
        +serviceId: ?string,
    |},
    +isContemporary: ?boolean,
    +isCustomizable: ?boolean,
    +isNewAndMadeToOrder: ?boolean,
    +classification: ?{|
        +categories: ?$ReadOnlyArray<?{|
            +category: ?{|
                +key: ?string,
                +browseUrl: ?string,
            |},
        |}>,
    |},
    +pricing: ?{|
        +amount: ?{|
            +amount: ?number,
        |},
        +netPrice: ?{|
            +amount: ?number,
        |},
        +salePrice: ?{|
            +amount: ?number,
        |},
    |},
};

export type TradeFirmFavoritesRootPortoflioItemsItem = $Call<
    <T>({
        +tradeFirmsDisplayMode?: ?$ReadOnlyArray<?{
            +publicProfile: ?{
                +portfoliosInflated: ?$ReadOnlyArray<?{
                    +items: ?{
                        +edges: ?$ReadOnlyArray<?{
                            +node: ?{
                                +item: ?T,
                            },
                        }>,
                    },
                }>,
            },
        }>,
    }) => T,
    FavoritesViewer
>;

type trackItemTileProps = {
    id: string,
    position: number,
    dimension76: string,
    dimension83: string,
    price: number | string,
    brand: string,
    category: string,
    categoryCode: string,
    name: string,
    list: string,
};

export const trackItemTile = ({
    id,
    position,
    dimension76,
    dimension83,
    price,
    brand,
    category,
    categoryCode,
    name,
    list,
}: trackItemTileProps) => {
    const trackObj = {
        type: 'productClick',
        products: {
            id,
            position,
            dimension76,
            dimension83,
            price,
            brand,
            category,
            categoryCode,
            name,
            list,
        },
        actionFieldList: list,
    };
    trackEcomEvent(trackObj);
};

export type ecomProductTrackingReturnType = {
    brand: string,
    category: string,
    categoryCode: string,
    dimension76: string,
    dimension83: string,
    id: string,
    position: number,
    price: number | string,
    name: string,
    list: string,
};

type productTrackingProps<T: OverviewFavoriteItemProps> = {
    isTrade: boolean,
    items: Array<?T>,
    listType?: string,
    positionNum?: number,
};

export const createProductImpressionTracking = <T: OverviewFavoriteItemProps>({
    isTrade,
    listType = 'designer favorites',
    items = [],
    positionNum,
}: productTrackingProps<T>): Array<ecomProductTrackingReturnType> => {
    return items.map((item, index) => {
        const itemGetter = getter(item);
        const isNewAndMadToOrder = itemGetter.value('isNewAndMadeToOrder') ? 'y' : 'n';
        const isContemporary = itemGetter.value('isContemporary') ? 'y' : 'n';
        const isCustomizable = itemGetter.value('isCustomizable') ? 'y' : 'n';
        const id = itemGetter.value('serviceId') || 'no item pk';
        const name = itemGetter.value('title') || 'item missing title';
        const position = positionNum === undefined ? index : positionNum;
        const dimension83 = `contemporary=${isContemporary}|cust=${isCustomizable}|new_and_made_to_order=${isNewAndMadToOrder}`;
        const brand = itemGetter.at('seller').value('serviceId') || 'no seller id';
        const categoryGetter = itemGetter
            .at('classification')
            .at('categories')
            .at(0)
            .at('category');
        const category = categoryGetter.value('browseUrl') || 'no category';
        const categoryCode = categoryGetter.value('key') || 'no category code';

        const priceGetter = itemGetter.at('pricing');
        const salePrice = priceGetter.at('salePrice').value('amount');
        let price;
        let dimension76;
        if (salePrice) {
            price = salePrice;
            dimension76 = 'saturday sale'; // should change to `sale price`
        } else if (isTrade) {
            price = priceGetter.at('netPrice').value('amount');
            dimension76 = 'net price';
        } else {
            price = priceGetter.at('amount').value('amount');
            dimension76 = 'retail price';
        }

        if (!price) {
            price = 'price missing';
        }

        return {
            brand,
            category,
            categoryCode,
            dimension76,
            dimension83,
            id,
            name,
            position,
            price,
            list: listType,
        };
    });
};
